import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from '../app.service';
import { UtilsService } from '../shared/utils.service';
import { AuditsModel } from './audits.model';
import { AuditsService } from './audits.service';
import { SitesSelectorComponent } from '../shared/sites-selector/sites-selector.component';
import { AuditsFilterComponent } from './audits-filter/audits-filter.component';
import { ApiDataSource } from '../utils/api-data-source';
import { ApiRequestService } from '../shared/api-request.service';
import { RBACService } from '../shared/rbac.service';
import { UserPublicProfileComponent } from '../shared/user-public-profile/user-public-profile.component';
import { ChartsUtilsService } from '../charts/charts-utils.service';

@Component({
  selector: 'app-audits',
  templateUrl: './audits.component.html',
  styleUrls: ['./audits.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AuditsComponent implements OnInit, AfterViewInit {
  // RBAC permission flags: resolved once in ngOnInit().
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new AuditsDataSource(this.app, this.api);

  // Build displayedColumns dynamically: NOT as a static array.
  displayedColumns: string[] = [];
  query: string;

  constructor(
    private auditsService: AuditsService,
    public utils: UtilsService,
    private app: AppService,
    private api: ApiRequestService,
    public chartsUtils: ChartsUtilsService,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_REGISTERS_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_REGISTERS_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_REGISTERS_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access inspections & audits.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [
      'id',
      'site',
      'name',
      'created_by_name',
      'created_by_employer',
      'completed',
      'percentCompleted',
      'date_created',
      'buttons'
    ];
    if (this.canView || this.canDelete) {
      this.displayedColumns.unshift('select');
    }

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.dataSource.getData(false);
    }
  }

  onRemoveSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete inspections & audits.'
      );
      return;
    }
    const selected = this.dataSource.selection.selected;
    this.utils.showModal(
      'Remove Inspections & Audits',
      `Are you sure you want to remove ${
        selected.length
      } Inspections & Audits?`,
      () => this.remove(selected)
    );
  }

  private remove(ids: number[]) {
    this.auditsService.remove(ids).subscribe(() => {
      this.utils.showToast('Inspections & Audits have been removed.');
      this.dataSource.selection.clear();
      this.dataSource.getData(false);
    });
  }

  onRemove(audit: AuditsModel) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete inspections & audits.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Inspection/Audit',
      `Are you sure you want to remove the "${audit.name}" Inspection/Audit?`,
      () => this.remove([audit.id])
    );
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === 'date_created_UTC') {
        this.dataSource.order_by = 'date_created';
      } else {
        this.dataSource.order_by = this.sort.active;
      }

      this.dataSource.getData(false);
    });
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onSelectSites() {
    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        selected: this.dataSource.site_ids,
        multiple: true
      },
      {
        width: '1024px'
      },
      (results) => {
        if (typeof results != 'undefined') {
          this.dataSource.site_ids = results;
          this.dataSource.getData();
        }
      }
    );
  }

  /**
   * Send a request to the API to export site audits as CSV/XLSX.
   * Duplicated code in: src/app/sites/sites-audits/sites-audits.component.ts. Should be refactored into a service file.
   */
  onExportSiteAudits(exportType: string = 'csv') {
    this.api
      .makeRequest(
        'get',
        'v2/site-audits/export/' + exportType,
        {},
        {
          site_ids: this.dataSource.site_ids.join(','),
          date_range:
            this.dataSource.date_range && this.dataSource.date_range.length > 1
              ? [
                  this.dataSource.date_range[0].getTime() / 1000,
                  this.dataSource.date_range[1].getTime() / 1000
                ].join(',')
              : '',
          selected_ids: this.dataSource.selection.selected.join(','), // To export selected records.
          site_audit_template_ids: this.dataSource.site_audit_template_ids.join(
            ','
          ),
          search: this.dataSource.search
        }
      )
      .then((response) => {
        // Check if we have a message response.
        this.utils.showToast(
          response.message ||
            'Your export is queued in the background. You will receive an email when it is ready.'
        );
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Send a request to the API to export site audits as PDF.
   * Duplicated code in: src/app/sites/sites-audits/sites-audits.component.ts. Should be refactored into a service file.
   */
  onExportSiteAuditPDFs() {
    this.api
      .makeRequest(
        'get',
        'v2/site-audits/export/pdf',
        {},
        {
          site_ids: this.dataSource.site_ids.join(','),
          date_range:
            this.dataSource.date_range && this.dataSource.date_range.length > 1
              ? [
                  this.dataSource.date_range[0].getTime() / 1000,
                  this.dataSource.date_range[1].getTime() / 1000
                ].join(',')
              : '',
          selected_ids: this.dataSource.selection.selected.join(','), // To export selected records.
          site_audit_template_ids: this.dataSource.site_audit_template_ids.join(
            ','
          ),
          search: this.dataSource.search
        }
      )
      .then((response) => {
        // Check if we have a message response.
        if (typeof response.message != 'undefined') {
          this.utils.showToast(response.message);
        }
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  onFilter() {
    this.utils.showComponentDialog(
      AuditsFilterComponent,
      {
        site_ids: this.dataSource.site_ids,
        date_range: this.dataSource.date_range,
        template_ids: this.dataSource.site_audit_template_ids
      },
      {
        width: '1024px'
      },
      (results) => {
        if (typeof results != 'undefined') {
          this.dataSource.site_ids = results.site_ids
            ? results.site_ids
            : this.dataSource.site_ids;
          this.dataSource.date_range = results.date_range
            ? results.date_range
            : this.dataSource.date_range;
          this.dataSource.site_audit_template_ids = results.template_ids
            ? results.template_ids
            : this.dataSource.site_audit_template_ids;
          this.dataSource.getData(true);
        }
      }
    );
  }

  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }
}

export class AuditsDataSource extends ApiDataSource {
  order_by = 'date_created';
  order = 'desc';

  site_ids = [];

  date_range: Date[] = [];

  // Allow filtering site audits by template names. Template names are retrieved in the API via these ids.
  site_audit_template_ids: number[] = [];

  getData(resetOffset: boolean = false) {
    this.makeRequest('v1/audits/reports', resetOffset, {
      site_ids: this.site_ids.join(','),
      date_range:
        this.date_range && this.date_range.length > 1
          ? [
              this.date_range[0].getTime() / 1000,
              this.date_range[1].getTime() / 1000
            ].join(',')
          : '',
      site_audit_template_ids: this.site_audit_template_ids.join(',')
    });
  }

  exportData(exportType: string = 'csv'): Promise<any> {
    return this.makeDownloadRequest(`v1/audits/reports/export/${exportType}`, {
      site_ids: this.site_ids.join(','),
      date_range:
        this.date_range && this.date_range.length > 1
          ? [
              this.date_range[0].getTime() / 1000,
              this.date_range[1].getTime() / 1000
            ].join(',')
          : '',
      selected_ids: this.selection.selected.join(','), // To export selected records.
      site_audit_template_ids: this.site_audit_template_ids.join(',')
    });
  }
}
